import React, { useState } from "react";
import { Link } from "react-router-dom";
import { typography } from "../../design-system/tokens";
import { useAffiliatePayoutStatus } from "../hooks/useAffiliatePayouts";
import { createPayoutOnboardingLink } from "../utils/affiliateApi";
import { formatMoney } from "../utils/affiliatePaymentFormat";
import { affiliateBrandGradientStops } from "../utils/uiClasses";

/**
 * Persistent banner until Express is payout-ready.
 * Shell placement: top of dashboard (except My Payments).
 * Embedded: above the My Payments page title, same placement as the shell banner on every other tab (no double CTA on that tab).
 */
const PayoutSetupReminder = ({ embedded = false, showPaymentsLink = true }) => {
  const statusQuery = useAffiliatePayoutStatus({ enabled: true });
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");

  const data = statusQuery.data;
  if (statusQuery.isLoading || statusQuery.error || !data) return null;
  if (data.account?.is_payout_ready) return null;

  const owedAmount = Number(data.owed?.amount || 0);
  const currency = data.owed?.currency || "usd";
  const hasOwed = Number.isFinite(owedAmount) && owedAmount > 0;

  const startOnboarding = async () => {
    setError("");
    setStarting(true);
    try {
      const result = await createPayoutOnboardingLink();
      if (result?.url) {
        window.location.assign(result.url);
        return;
      }
      setError("Could not start payout setup.");
    } catch (e) {
      const detail = e?.data?.detail;
      const message = e?.message || "Could not start payout setup.";
      setError(detail ? `${message} ${detail}` : message);
    } finally {
      setStarting(false);
    }
  };

  const banner = (
    <div
      className={`relative overflow-hidden rounded-2xl bg-gradient-to-r ${affiliateBrandGradientStops} px-4 py-3.5 sm:px-6 sm:py-4 shadow-lg shadow-purple-500/30 ring-1 ring-white/25`}
      role="status"
      aria-live="polite"
    >
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/30 via-white/5 to-transparent"
        aria-hidden="true"
      />
      <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <p className="text-base font-medium leading-relaxed text-white">
            {hasOwed
              ? `You have ${formatMoney(owedAmount, currency)} waiting — set up your payout account to receive it.`
              : "Set up your payout account to receive commission payments."}
          </p>
          <p className="mt-1 text-xs tracking-wide text-white/85">
            {showPaymentsLink ? (
              <>
                Connect Stripe Express from{" "}
                <Link
                  to="/affiliate/dashboard/payments"
                  className="underline decoration-white/70 underline-offset-2 hover:decoration-white hover:text-white"
                >
                  My Payments
                </Link>
                .
              </>
            ) : (
              "Connect Stripe Express to receive your commission payouts."
            )}
          </p>
          {error ? <p className="mt-1 text-xs tracking-wide text-amber-100">{error}</p> : null}
        </div>
        <button
          type="button"
          onClick={startOnboarding}
          disabled={starting}
          className={`shrink-0 px-5 py-2.5 rounded-xl ${typography.button} bg-white text-purple-800 shadow-md hover:bg-white/95 hover:shadow-lg hover:scale-[1.02] active:scale-[0.99] transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-fuchsia-600 disabled:opacity-50 disabled:hover:scale-100 disabled:cursor-not-allowed`}
        >
          {starting ? "Starting…" : data.account?.exists ? "Continue setup" : "Set up payouts"}
        </button>
      </div>
    </div>
  );

  if (embedded) return banner;

  return (
    <div className="p-4 sm:p-6 pb-0">
      <div className="max-w-7xl mx-auto">{banner}</div>
    </div>
  );
};

export default PayoutSetupReminder;
